import React from 'react';
import { ChevronDown } from 'lucide-react';
import { getInitials } from './avatar-utils';

const OPTICAL_ADJUSTMENTS: Record<string, { y: string; x: string }> = {
  A: { y: '-0.12em', x: '0' },
  C: { y: '-0.045em', x: '-0.02em' },
  D: { y: '-0.045em', x: '0.05em' },
  F: { y: '-0.045em', x: '0.02em' },
  G: { y: '-0.045em', x: '-0.02em' },
  J: { y: '-0.18em', x: '0.04em' },
  L: { y: '-0.045em', x: '0.04em' },
  P: { y: '-0.045em', x: '0.04em' },
  Q: { y: '-0.12em', x: '-0.01em' },
  T: { y: '-0.02em', x: '0' },
  U: { y: '-0.01em', x: '-0.01em' },
  V: { y: '-0.02em', x: '0' },
  W: { y: '0.03em', x: '0' },
  X: { y: '-0.04em', x: '-0.0em' },
  Y: { y: '-0.02em', x: '0' },
  default: { y: '-0.025em', x: '0.015em' },
};

export const AVATAR_BUTTON_DIAMETERS = {
  topMenu: 62,
  sideMenu: 66,
  // Fits the collapsed sidebar rail (40px matches the nav button size).
  sideMenuCollapsed: 50,
} as const;

export const AVATAR_CHEVRON_DIAMETERS = {
  topMenu: 20,
  sideMenu: 14,
  sideMenuCollapsed: 10,
} as const;

// Controls how thick the outer gradient ring appears relative to diameter.
// Lower values produce a thinner ring. Tweak this number to tune visuals.
export const AVATAR_RING_RATIO = 0.03;

// Control how far the badge is offset from the avatar's edge (as a ratio
// of the avatar diameter). Lower values move the badge inward toward the
// avatar; higher values move it further out. Tweak these per-surface.
export const AVATAR_BADGE_OFFSET_RATIOS = {
  topMenu: 0.08,
  sideMenu: 0.12,
} as const;

export type AvatarButtonProps = {
  initial?: string;
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  theme?: 'light' | 'dark';
  onClick?: () => void;
  className?: string;
  showChevron?: boolean;
  size?: 'sm' | 'md';
  /**
   * Optional numeric size (pixels) or CSS size string (e.g. '32px' or '2rem').
   * When provided, this single value controls the outer diameter and scales
   * the inner core, text and chevron proportionally.
   */
  diameter?: number | string;
  /** Explicit chevron/badge diameter in pixels or CSS string (e.g. '18px'). Overrides computed badge size when provided. */
  badgeDiameter?: number | string;
  chevronDirection?: 'down' | 'up';
  ariaLabel?: string;
  as?: 'button' | 'div';
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'className' | 'aria-label' | 'type'>;

/* helpers moved to src/components/ui/avatar-utils.ts to keep this file
   exporting only components for fast refresh compatibility */

function getAdjustment(value: string) {
  const firstChar = value.charAt(0).toUpperCase();
  return OPTICAL_ADJUSTMENTS[firstChar] || OPTICAL_ADJUSTMENTS.default;
}

function AvatarChevronBadge({
  isDark,
  chevronDirection = 'down',
  iconSize = 11,
  style,
}: {
  isDark: boolean;
  chevronDirection?: 'down' | 'up';
  iconSize?: number;
  style?: React.CSSProperties;
}) {
  return (
    <div
      data-testid="account-menu-chevron-badge"
      aria-hidden
      className="pointer-events-none absolute left-1/2 bottom-[-0.25rem] z-40 -translate-x-1/2"
      style={style}
    >
      <div
        className={[
          'relative flex h-full w-full items-center justify-center rounded-full border-2 shadow-lg transition-all duration-300',
          isDark
            ? 'border-[#0a0c14] bg-[#3b82f6] shadow-black/50'
            : 'border-white bg-[#3b82f6] shadow-blue-500/20',
        ].join(' ')}
      >
        <ChevronDown
          size={iconSize}
          className={`text-white transition-transform duration-300 ${chevronDirection === 'up' ? 'rotate-180' : ''}`}
          strokeWidth={3.5}
        />
        <div className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-white/25 to-transparent" />
        <div className="pointer-events-none absolute inset-0 rounded-full shadow-[inset_0_-2px_4px_rgba(0,0,0,0.1)]" />
      </div>
    </div>
  );
}

const sizeMap = {
  sm: {
    outer: 'h-9 w-9',
    ringInset: 'inset-[3px]',
    core: 'h-[26px] w-[26px]',
    text: 'text-[1.05rem]',
    chevron: 'h-4 w-4 -bottom-0.5 -right-0.5 border-[2px]',
    chevronIcon: 9,
  },
  md: {
    outer: 'h-10 w-10',
    ringInset: 'inset-[3px]',
    core: 'h-[31px] w-[31px]',
    text: 'text-[1.28rem]',
    chevron: 'h-5 w-5 -bottom-1 -right-1 border-[2px]',
    chevronIcon: 10,
  },
} as const;

export const AvatarButton = React.forwardRef<HTMLButtonElement | HTMLDivElement, AvatarButtonProps>(function AvatarButton(
  {
    initial,
    name,
    email,
    avatarUrl,
    theme = 'dark',
    onClick,
    className = '',
    showChevron = true,
    size = 'md',
    diameter,
    badgeDiameter,
    chevronDirection = 'down',
    ariaLabel = 'Account menu',
    as = 'button',
    ...rest
  },
  ref,
) {
  const initials = getInitials(name, email, initial);
  const adj = getAdjustment(initials);
  const sizes = sizeMap[size];
  const isDark = theme !== 'light';
  // dynamic sizing support: if `diameter` is provided, compute inline
  // styles and derived sizes (core, text, chevron). Otherwise fall back to
  // the existing `sizeMap` classes. `badgeDiameter` if provided overrides
  // the computed chevron/badge size.
  const providedDiameter = typeof diameter !== 'undefined' ? diameter : undefined;
  const providedBadgeDiameter = typeof badgeDiameter !== 'undefined' ? badgeDiameter : undefined;

  let outerStyle: React.CSSProperties | undefined;
  let innerInsetStyle: React.CSSProperties | undefined;
  let coreStyle: React.CSSProperties | undefined;
  let textStyle: React.CSSProperties | undefined;
  let chevronStyle: React.CSSProperties | undefined;
  let chevronIconSize: number = sizes.chevronIcon as number;

  if (typeof providedDiameter !== 'undefined' && providedDiameter !== null) {
    const parsed = typeof providedDiameter === 'number' ? providedDiameter : parseFloat(String(providedDiameter).replace('px', ''));
    const d = Number.isFinite(parsed) && parsed > 0 ? parsed : (size === 'md' ? 40 : 36);

    const ringPx = Math.max(1, Math.round(d * AVATAR_RING_RATIO));
    const corePx = Math.max(8, Math.round(d * 0.78));
    const textPx = Math.max(8, Math.round(corePx * 0.65));
    let badgePx = Math.max(10, Math.round(d * 0.3));
    // decide offset ratio based on known presets (top/side) or default
    const defaultOffsetRatio = 0.12;
    let offsetRatio = defaultOffsetRatio;
    if (d === AVATAR_BUTTON_DIAMETERS.topMenu) offsetRatio = AVATAR_BADGE_OFFSET_RATIOS.topMenu;
    else if (d === AVATAR_BUTTON_DIAMETERS.sideMenu) offsetRatio = AVATAR_BADGE_OFFSET_RATIOS.sideMenu;
    const badgeOffset = Math.round(d * offsetRatio);
    let chevIcon = Math.max(8, Math.round(d * 0.3));

    // If an explicit badge diameter was provided, override the computed one.
    if (typeof providedBadgeDiameter !== 'undefined' && providedBadgeDiameter !== null) {
      const parsedBadge = typeof providedBadgeDiameter === 'number' ? providedBadgeDiameter : parseFloat(String(providedBadgeDiameter).replace('px', ''));
      if (Number.isFinite(parsedBadge) && parsedBadge > 0) {
        badgePx = parsedBadge;
      }
    }

    // icon size scales with badge size
    chevIcon = Math.max(6, Math.round(badgePx * 0.6));

    outerStyle = { width: d, height: d };
    innerInsetStyle = { top: ringPx, left: ringPx, right: ringPx, bottom: ringPx };
    coreStyle = { width: corePx, height: corePx };
    textStyle = { fontSize: `${textPx}px` };
    chevronStyle = { width: badgePx, height: badgePx, bottom: -badgeOffset };
    chevronIconSize = chevIcon;
  } else if (typeof providedBadgeDiameter !== 'undefined' && providedBadgeDiameter !== null) {
    // No overall diameter supplied, but explicit badge size provided.
    const parsedBadge = typeof providedBadgeDiameter === 'number' ? providedBadgeDiameter : parseFloat(String(providedBadgeDiameter).replace('px', ''));
    if (Number.isFinite(parsedBadge) && parsedBadge > 0) {
      const badgePx = parsedBadge;
      const offset = Math.max(2, Math.round(badgePx * 0.25));
      chevronStyle = { width: badgePx, height: badgePx, bottom: -offset };
      chevronIconSize = Math.max(6, Math.round(badgePx * 0.6));
    }
  }
  const outerGlowClassName = isDark
    ? 'absolute inset-0 rounded-full bg-[conic-gradient(from_210deg,#4f8df7_0deg,#5b8cff_72deg,#9b5cf5_198deg,#f472b6_312deg,#4f8df7_360deg)] shadow-[0_0_8px_rgba(124,58,237,0.32),0_0_12px_rgba(79,70,229,0.14)] transition-shadow duration-300 group-hover:shadow-[0_0_14px_rgba(96,165,250,0.44),0_0_20px_rgba(96,165,250,0.22)]'
    : 'absolute inset-0 rounded-full bg-[conic-gradient(from_210deg,#60a5fa_0deg,#7c88ff_72deg,#a78bfa_198deg,#f472b6_312deg,#60a5fa_360deg)] shadow-[0_0_6px_rgba(96,165,250,0.16),0_0_12px_rgba(124,58,237,0.12)] transition-shadow duration-300 group-hover:shadow-[0_0_10px_rgba(96,165,250,0.22),0_0_16px_rgba(124,58,237,0.16)]';
  const innerInsetClassName = isDark
    ? 'absolute rounded-full bg-[#04070d]'
    : 'absolute rounded-full bg-[linear-gradient(180deg,rgba(255,255,255,0.94)_0%,rgba(241,245,249,0.97)_100%)]';
  const coreClassName = isDark
    ? `relative z-10 ${coreStyle ? '' : sizes.core} flex items-center justify-center overflow-hidden rounded-full border border-white/28 bg-[radial-gradient(circle_at_32%_18%,rgba(255,255,255,0.24),rgba(255,255,255,0.06)_28%,rgba(16,18,29,0.96)_68%,rgba(8,10,17,1)_100%)] shadow-[inset_0_1px_6px_rgba(255,255,255,0.18),inset_0_-10px_20px_rgba(94,83,196,0.12),0_10px_18px_rgba(0,0,0,0.38)] transition-transform duration-300 group-hover:scale-[1.02]`
    : `relative z-10 ${coreStyle ? '' : sizes.core} flex items-center justify-center overflow-hidden rounded-full border border-slate-200/90 bg-[radial-gradient(circle_at_32%_18%,rgba(255,255,255,0.98),rgba(248,250,252,0.82)_34%,rgba(226,232,240,0.78)_68%,rgba(203,213,225,0.96)_100%)] shadow-[inset_0_1px_6px_rgba(255,255,255,0.72),inset_0_-10px_18px_rgba(148,163,184,0.10),0_10px_18px_rgba(15,23,42,0.10)] transition-transform duration-300 group-hover:scale-[1.02]`;
  const coreWashClassName = isDark
    ? 'pointer-events-none absolute inset-[1px] rounded-full shadow-[inset_0_14px_20px_rgba(255,255,255,0.05),inset_0_-10px_14px_rgba(0,0,0,0.22)]'
    : 'pointer-events-none absolute inset-[1px] rounded-full shadow-[inset_0_14px_20px_rgba(255,255,255,0.16),inset_0_-10px_14px_rgba(148,163,184,0.10)]';
  const initialsClassName = isDark
    ? `${textStyle ? '' : sizes.text} relative z-20 select-none font-serif font-medium leading-none text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.55)]`
    : `${textStyle ? '' : sizes.text} relative z-20 select-none font-serif font-medium leading-none text-slate-600 drop-shadow-[0_1px_2px_rgba(255,255,255,0.85)]`;
  const focusOffsetClassName = isDark ? 'focus-visible:ring-offset-[#05070e]' : 'focus-visible:ring-offset-white';
  const inner = (
    <span
      className={`relative ${outerStyle ? '' : sizes.outer} flex items-center justify-center overflow-visible rounded-full`}
      style={outerStyle}
    >
      <span className={outerGlowClassName} />
      <span className={`${innerInsetClassName} ${innerInsetStyle ? '' : 'inset-[1px]'}`} style={innerInsetStyle} />
      <span className={coreClassName} style={coreStyle}>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="absolute inset-0 h-full w-full rounded-full object-cover"
          />
        ) : (
          <>
            <span className={isDark ? 'pointer-events-none absolute inset-[3%] rounded-full bg-[radial-gradient(circle_at_50%_8%,rgba(255,255,255,0.46),rgba(255,255,255,0.18)_24%,rgba(255,255,255,0.05)_42%,transparent_64%)] opacity-95' : 'pointer-events-none absolute inset-[3%] rounded-full bg-[radial-gradient(circle_at_50%_8%,rgba(255,255,255,0.92),rgba(255,255,255,0.44)_24%,rgba(255,255,255,0.08)_42%,transparent_64%)] opacity-95'} />
            <span className={isDark ? 'pointer-events-none absolute left-[-14%] top-[18%] h-[56%] w-[56%] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.1),transparent_72%)] opacity-70' : 'pointer-events-none absolute left-[-14%] top-[18%] h-[56%] w-[56%] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.75),transparent_72%)] opacity-55'} />
            <span className={isDark ? 'pointer-events-none absolute -right-[12%] top-[8%] h-[84%] w-[58%] rotate-[18deg] rounded-full bg-[linear-gradient(135deg,rgba(255,255,255,0.02)_0%,rgba(13,16,25,0.12)_38%,rgba(0,0,0,0.52)_100%)] opacity-85' : 'pointer-events-none absolute -right-[12%] top-[8%] h-[84%] w-[58%] rotate-[18deg] rounded-full bg-[linear-gradient(135deg,rgba(255,255,255,0.34)_0%,rgba(226,232,240,0.24)_38%,rgba(148,163,184,0.12)_100%)] opacity-85'} />
            <span className="pointer-events-none absolute inset-0 rounded-full border border-white/16 shadow-[inset_0_1px_2px_rgba(255,255,255,0.14)]" />
            <span className={initialsClassName} style={{ ...(textStyle || {}), transform: `translate(${adj.x}, ${adj.y})` }}>
              {initials}
            </span>
          </>
        )}
        <span className={coreWashClassName} />
      </span>

      {showChevron && <AvatarChevronBadge isDark={isDark} chevronDirection={chevronDirection} iconSize={chevronIconSize} style={chevronStyle} />}
    </span>
  );

  if (as === 'div') {
    return (
      <div
        {...(rest as React.HTMLAttributes<HTMLDivElement>)}
        ref={ref as React.Ref<HTMLDivElement>}
        aria-label={ariaLabel}
        className={`relative group inline-flex shrink-0 items-center justify-center rounded-full bg-transparent p-0 outline-none transition-transform duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-400/70 focus-visible:ring-offset-2 ${focusOffsetClassName} ${className}`}
      >
        {inner}
      </div>
    );
  }

  return (
    <button
      {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={`relative group inline-flex shrink-0 items-center justify-center rounded-full bg-transparent p-0 outline-none transition-transform duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-400/70 focus-visible:ring-offset-2 ${focusOffsetClassName} ${className}`}
    >
      {inner}
    </button>
  );
});

export default AvatarButton;
